// Cockpit revamp — status transition confirm modal.
//
// Wraps the generic ConfirmReasonModal but adds the LT-348 §2.4 #4
// `also_cancel_callbacks` toggle for the force-end target. The base
// ConfirmReasonModal stays generic; this thin wrapper attaches the
// toggle as the `extra` slot.
import React, { useState } from 'react';

import { Callout, ConfirmReasonModal } from '../../../ui';
import type { Campaign } from '../../../types/campaign';
import type { TransitionDef } from './transitions';

export const FORCE_END_WARNING =
  'Force-ending stops in-flight steps but leaves any pending callbacks '
  + 'committed under this campaign intact. They will still fire as scheduled.';

interface TransitionConfirmModalProps {
  open: boolean;
  definition: TransitionDef;
  campaign: Campaign;
  saving?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: (reason: string, alsoCancelCallbacks: boolean) => void;
}

export const TransitionConfirmModal: React.FC<TransitionConfirmModalProps> = ({
  open,
  definition,
  campaign,
  saving,
  error,
  onCancel,
  onConfirm,
}) => {
  const [alsoCancelCallbacks, setAlsoCancelCallbacks] = useState(false);
  const isForceEnd = definition.kind === 'force_end';
  const isLaunch = definition.kind === 'launch';

  // Reset the also-cancel toggle whenever the modal re-opens for a
  // different transition.
  React.useEffect(() => {
    if (open) setAlsoCancelCallbacks(false);
  }, [open, definition.kind]);

  return (
    <ConfirmReasonModal
      open={open}
      title={`${definition.verb}?`}
      description={
        <>
          {definition.note}
          {isLaunch ? (
            <span className="block mt-1 text-slate-500">
              Snapshot the audience, pin the sequence at v
              {campaign.sequence_version_at_launch ?? '—'}, and start
              dispatching.
            </span>
          ) : null}
        </>
      }
      extra={
        isForceEnd ? (
          <>
            <Callout tone="warn" title="Heads up">
              {FORCE_END_WARNING}
            </Callout>
            <label className="inline-flex items-start gap-2 text-sm text-slate-300 mt-3">
              <input
                type="checkbox"
                checked={alsoCancelCallbacks}
                onChange={(e) => setAlsoCancelCallbacks(e.target.checked)}
                className="mt-0.5 accent-accent"
              />
              <span>
                Also cancel pending callbacks committed under this campaign.
                <span className="block text-xs text-slate-500">
                  Default off — operators usually want callbacks to survive.
                </span>
              </span>
            </label>
          </>
        ) : null
      }
      confirmLabel={`Confirm · ${definition.label}`}
      confirmIntent={definition.intent}
      saving={saving}
      error={error}
      onCancel={onCancel}
      onConfirm={(reason) => onConfirm(reason, alsoCancelCallbacks)}
    />
  );
};

export default TransitionConfirmModal;
